import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from '@dashboard/components/ProtectedRoute';
import DashboardLayout from '@dashboard/layouts/DashboardLayout';
import Landing from '@dashboard/pages/Landing';
import Login from '@dashboard/pages/Login';
import Register from '@dashboard/pages/Register';
import Apply from '@dashboard/pages/Apply';
import HomeDashboard from '@dashboard/home/HomeDashboard';
import CryptoOverview from '@dashboard/crypto/CryptoOverview';
import CryptoProviders from '@dashboard/crypto/CryptoProviders';
import StripeOverview from '@dashboard/stripe/StripeOverview';
import SquareOverview from '@dashboard/square/SquareOverview';
import SquareWebhookEventsPage from '@dashboard/square/SquareWebhookEventsPage';
import Orders from '@dashboard/shared/Orders';
import OrderDetail from '@dashboard/shared/OrderDetail';
import Payouts from '@dashboard/shared/Payouts';
import Invoices from '@dashboard/shared/Invoices';
import Reports from '@dashboard/shared/Reports';
import SettingsPage from '@dashboard/settings/SettingsPage';

import Privacy from '@dashboard/pages/Privacy';
import Terms from '@dashboard/pages/Terms';
import ComingSoon from '@dashboard/shared/ComingSoon';
import ApiDocs from '@dashboard/pages/ApiDocs';
import AdminLogin from '@dashboard/admin/pages/AdminLogin';
import AdminProtectedRoute from '@dashboard/admin/components/AdminProtectedRoute';
import AdminDashboardLayout from '@dashboard/admin/layouts/AdminDashboardLayout';
import AdminOverview from '@dashboard/admin/pages/AdminOverview';
import AdminApplications from '@dashboard/admin/pages/AdminApplications';
import AdminMerchants from '@dashboard/admin/pages/AdminMerchants';
import AdminOrders from '@dashboard/admin/pages/AdminOrders';
import AdminRevenue from '@dashboard/admin/pages/AdminRevenue';
import AdminSettings from '@dashboard/admin/pages/AdminSettings';
import AdminAuditLogPage from '@dashboard/admin/pages/AdminAuditLogPage';
import StripeOnboardingComplete from '@dashboard/stripe/StripeOnboardingComplete';
import StripeOnboardingRefresh from '@dashboard/stripe/StripeOnboardingRefresh';
import StripeInvoices from '@dashboard/stripe/invoices/StripeInvoices';

import CheckoutSkeleton from '@checkout/CheckoutSkeleton';
import CryptoWalletErrorBoundary from '@dashboard/crypto/CryptoWalletErrorBoundary';
import CryptoLockGuard from '@dashboard/crypto/CryptoLockGuard';
const HostedCheckout = lazy(() => import('@checkout/HostedCheckout'));
const CryptoWallet = lazy(() => import('@dashboard/crypto/CryptoWallet'));
const SolanaProvider = lazy(() => import('@dashboard/crypto/SolanaProvider'));

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/apply" element={<Apply />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/api-docs" element={<ApiDocs />} />
      <Route path="/status" element={<ComingSoon />} />
      <Route path="/blog" element={<ComingSoon />} />
      <Route path="/contact" element={<ComingSoon />} />
      <Route path="/stripe/onboarding-complete" element={<StripeOnboardingComplete />} />
      <Route path="/stripe/onboarding-refresh" element={<StripeOnboardingRefresh />} />
      <Route path="/pay/:orderId" element={<Suspense fallback={<CheckoutSkeleton />}><HostedCheckout /></Suspense>} />
      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          <Route path="/dashboard" element={<Navigate to="/dashboard/home" replace />} />
          <Route path="/dashboard/home" element={<HomeDashboard />} />

          <Route path="/dashboard/crypto" element={<CryptoLockGuard />}>
            <Route index element={<CryptoOverview />} />
            <Route path="orders" element={<Orders solution="crypto" />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route
              path="wallet"
              element={
                <Suspense fallback={<div className="p-6 text-sm" style={{ color: 'var(--dash-text-muted)' }}>Loading wallet...</div>}>
                  <CryptoWalletErrorBoundary>
                    <SolanaProvider>
                      <CryptoWallet />
                    </SolanaProvider>
                  </CryptoWalletErrorBoundary>
                </Suspense>
              }
            />
            <Route path="providers" element={<CryptoProviders />} />
            <Route path="payouts" element={<Payouts solution="crypto" />} />
            <Route path="invoices" element={<Invoices solution="crypto" />} />
            <Route path="reports" element={<Reports solution="crypto" />} />
          </Route>

          <Route path="/dashboard/stripe" element={<StripeOverview />} />
          <Route path="/dashboard/stripe/orders" element={<Orders solution="stripe" />} />
          <Route path="/dashboard/stripe/orders/:id" element={<OrderDetail />} />
          <Route path="/dashboard/stripe/payouts" element={<Payouts solution="stripe" />} />
          <Route path="/dashboard/stripe/invoices" element={<StripeInvoices />} />
          <Route path="/dashboard/stripe/reports" element={<Reports solution="stripe" />} />

          <Route path="/dashboard/square" element={<SquareOverview />} />
          <Route path="/dashboard/square/orders" element={<Orders solution="square" />} />
          <Route path="/dashboard/square/orders/:id" element={<OrderDetail />} />
          <Route path="/dashboard/square/payouts" element={<Payouts solution="square" />} />
          <Route path="/dashboard/square/invoices" element={<Invoices solution="square" />} />
          <Route path="/dashboard/square/reports" element={<Reports solution="square" />} />
          <Route path="/dashboard/square/webhooks" element={<SquareWebhookEventsPage />} />

          <Route path="/dashboard/settings" element={<SettingsPage />} />
        </Route>
      </Route>
      <Route path="/04062001/admin-login" element={<AdminLogin />} />
      <Route element={<AdminProtectedRoute />}>
        <Route element={<AdminDashboardLayout />}>
          <Route path="/04062001/admin" element={<AdminOverview />} />
          <Route path="/04062001/admin/applications" element={<AdminApplications />} />
          <Route path="/04062001/admin/merchants" element={<AdminMerchants />} />
          <Route path="/04062001/admin/orders" element={<AdminOrders />} />
          <Route path="/04062001/admin/revenue" element={<AdminRevenue />} />
          <Route path="/04062001/admin/settings" element={<AdminSettings />} />
          <Route path="/04062001/admin/audit-log" element={<AdminAuditLogPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
